import { ColumnTypes, fixedOrderColumns, getColumnId, getColumnNameFromId, isColumnDynamic } from "../utils/ProcessTableColumnsUtils";
import { ProcessTableColumnZod, SQLGeneratedProcess } from "@avenueops/shared-types";
import { XMarkIcon, EllipsisVerticalIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { ColumnType } from "@prisma/client";
import { Column, ColumnOrderState, Table } from "@tanstack/react-table";
import { useProcessTableColumns } from "context/ProcessTableContext";
import { useState, useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";
import { TableRow } from "../ProcessTableUtils";

interface Props {
  table: Table<TableRow>;
}

export function DraggableColumnTitles({ table }: Props) {
  const tableColumns = table.getAllLeafColumns();

  const [columnDragging, setColumnDragging] = useState<boolean>(false);
  return (
    <div className={`flex flex-col ${columnDragging ? "divide-y border-cool-gray-300" : ""}`}>
      {tableColumns.map((column) => {
        if (fixedOrderColumns.includes(column.id)) {
          return null;
        }
        return <DraggableColumnTitle key={column.id} table={table} column={column} setColumnDragging={setColumnDragging} />;
      })}
    </div>
  );
}

interface ItemProps {
  column: Column<TableRow>;
  table: Table<TableRow>;
  setColumnDragging: any;
}

function DraggableColumnTitle({ column, table, setColumnDragging }: ItemProps) {
  const { getState, setColumnOrder } = table;
  const { columnOrder } = getState();

  const [contextColumns, setContextColumns] = useProcessTableColumns();
  const ERROR_COLUMN = "META_ERROR";

  const [{ isHovering }, dropRef] = useDrop({
    collect: (monitor) => ({
      isHovering: monitor.isOver({ shallow: true }),
    }),
    accept: "column",
    drop: (draggedColumn: Column<SQLGeneratedProcess>) => {
      const newColumnOrder = reorderColumn(draggedColumn.id, column.id, columnOrder);
      setColumnOrder(newColumnOrder);
      setContextColumns(
        // error is random here -- should never occur
        newColumnOrder.map((colId) => contextColumns.find((col) => col.id === colId) ?? { id: getColumnId(ColumnTypes.META, "ERROR"), visibility: false }),
      );
    },
  });

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    item: () => column,
    type: "column",
  });

  useEffect(() => {
    setColumnDragging(isDragging);
  }, [isDragging, setColumnDragging]);

  const toggleColumnVisibility = (tableColumn: Column<TableRow>) => {
    const currVisibility = tableColumn.getIsVisible();

    if (currVisibility) {
      const updatedColumns = contextColumns.map((contextCol) =>
        contextCol.id === tableColumn.id ? ({ id: contextCol.id, visibility: false } as ProcessTableColumnZod) : contextCol,
      );
      setContextColumns(updatedColumns);
      tableColumn.toggleVisibility(false);
    } else {
      const updatedColumns = contextColumns.map((contextCol) =>
        contextCol.id === tableColumn.id ? ({ id: contextCol.id, visibility: true } as ProcessTableColumnZod) : contextCol,
      );
      setContextColumns(updatedColumns);
      tableColumn.toggleVisibility(true);
    }
  };

  const removeDynamicColumn = (columnId: string) => {
    const filteredColumns = contextColumns.filter((col: ProcessTableColumnZod) => col.id !== columnId);
    setContextColumns(filteredColumns);
  };

  return (
    <div ref={dropRef}>
      <div ref={previewRef} className={isDragging ? "border-cool-gray-300" : ""}>
        <div ref={dragRef} className={`group hover:bg-gray-50 py-1.5 text-sm ${isHovering ? "bg-gray-50 border-t-2 border-cool-gray-300" : ""}`}>
          <div key={column.id}>
            <label className="pr-2 flex flex-row items-center w-full hover:bg-gray-50">
              <div className="pr-1 group-hover:visible flex flex-row invisible">
                <EllipsisVerticalIcon className="w-4 h-4 text-gray-500" />
                <EllipsisVerticalIcon className="w-4 h-4 -ml-3 text-gray-500" />
              </div>
              {column.getIsVisible() ? (
                <EyeIcon onClick={() => toggleColumnVisibility(column)} className="h-5 w-5 mr-2 cursor-pointer text-gray-500 hover:text-gray-700" />
              ) : (
                <EyeSlashIcon onClick={() => toggleColumnVisibility(column)} className="h-5 w-5 mr-2 cursor-pointer text-gray-500 hover:text-gray-700" />
              )}
              <div className="flex flex-row w-full justify-between text-gray-500 cursor-grab">
                {getColumnNameFromId(column.id)}
                <div className="flex flex-row">
                  {isColumnDynamic(column.id) ? (
                    <div key={column.id} className="px-1 flex flex-row items-center">
                      <XMarkIcon className="w-4 h-4 text-gray-500 cursor-pointer" onClick={() => removeDynamicColumn(column.id)} />
                    </div>
                  ) : null}
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function reorderColumn(draggedColumnId: string, targetColumnId: string, columnOrder: string[]): ColumnOrderState {
  columnOrder.splice(columnOrder.indexOf(targetColumnId), 0, columnOrder.splice(columnOrder.indexOf(draggedColumnId), 1)[0] as string);
  return [...columnOrder];
}
